export interface MentionUser {
  id: number;
  firstname?: string;
  lastname?: string;
  email?: string;
}

export const MENTION_SCHEME = 'flowenti-user:';
export const MENTION_ID_REGEX = /flowenti-user:(\d+)/g;

export function buildMentionMarkdown(user: MentionUser): string {
  const label =
    `${user.firstname ?? ''} ${user.lastname ?? ''}`.trim() ||
    user.email ||
    String(user.id);
  return `[@${label}](${MENTION_SCHEME}${user.id})`;
}

/**
 * Rileva il token "@query" immediatamente prima del cursore.
 * Il carattere prima di '@' deve essere un inizio stringa o uno spazio
 * (così un'email come `email@x` non apre il popup).
 * Ritorna il testo della query e l'indice di inizio del token '@'
 * (utile per selezionare il range da sostituire).
 */
export function extractMentionQuery(
  textBeforeCaret: string
): { query: string; start: number } | null {
  const match = /(^|\s)@([^\s@{}()[\]]*)$/.exec(textBeforeCaret);
  if (!match) return null;
  return { query: match[2], start: match.index + match[1].length };
}

/**
 * Sostituisce l'ultimo token "@query" digitato con il markdown della menzione
 * scelta. Deterministico: non dipende dalla selezione DOM (che si perde quando
 * il popover prende il focus), quindi non lascia mai un "@" orfano.
 */
export function replaceMentionToken(
  markdown: string,
  query: string,
  mentionMarkdown: string
): string {
  const token = `@${query}`;
  const idx = markdown.lastIndexOf(token);
  return idx >= 0
    ? markdown.slice(0, idx) + mentionMarkdown + markdown.slice(idx + token.length)
    : markdown + mentionMarkdown;
}

/**
 * Id degli utenti menzionati nel testo markdown di un commento.
 *
 * Stesso contratto di `ExtractMentionedUserIds` (Flowenti.Notification.Helpers.pas):
 * stesso schema, stessa sequenza di cifre dopo di esso, e **stesso filtro**
 * — `TryStrToInt(lIdText, lId) and (lId > 0)`, cioè interi positivi a 32 bit.
 * Senza il filtro i due lati vedrebbero id diversi (`flowenti-user:0` o un id
 * fuori range passerebbero qui e verrebbero scartati dal Delphi) e la notifica
 * finirebbe su uno snapshot che nessuno legge.
 *
 * `Array.from` e non `for…of`: il tsconfig del repo ha `target: es5`.
 * `matchAll` e non `.exec` in un ciclo: non tocca il `lastIndex` della regex
 * condivisa, quindi non perde id fra due chiamate consecutive.
 */
export function extractMentionedUserIds(text: string): number[] {
  const ids: number[] = [];
  for (const match of Array.from(text.matchAll(MENTION_ID_REGEX))) {
    const id = Number(match[1]);
    if (id > 0 && id <= 2 ** 31 - 1 && !ids.includes(id)) ids.push(id);
  }
  return ids;
}
